import { isMessageLocked } from "../../common/messageVisibility";
import { getBookingStatusLabel } from "../../common/bookingStatus";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Layer,
  Pagination,
  Select,
  Spinner,
  Text,
  TextArea,
  TextInput,
} from "grommet";
import { FormClose, FormPrevious, Search, User } from "grommet-icons";
import moment from "moment-timezone";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CommunicationService } from "@services";
import { presentToastErrorContent } from "@shared/Toast";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import { PackageUpgradeModal } from "./components/OfferGate";
import MessagePhotoInput from "./components/MessagePhotoInput";
import Conversation from "./GuestDetails/components/Conversation";
import { useFetchHotel, useSetCurrentUser } from "./redux/hooks";
import { avatarColor, initials } from "./avatar";
import { normalizeStaffDepartment, staffDepartmentLabel } from "./staffDepartments";
import "./ConversationsPage.scss";

const FILTERS = ["all", "unread", "in-stay", "upcoming", "past"];
const PAGE_SIZE = 30;
const formatLastMessageDate = (date, t) => {
  const messageDate = moment(date);
  if (messageDate.isSame(moment(), "day")) return messageDate.format("LT");
  if (messageDate.isSame(moment().subtract(1, "day"), "day")) {
    return t("conversation.yesterday");
  }
  return messageDate.format("MMM D");
};

function NotifyStaffModal({ booking, onClose, onSent, onUpgrade }) {
  const { t } = useTranslation();
  const { currentUser } = useSetCurrentUser();
  const [staff, setStaff] = useState([]);
  const [targetMode, setTargetMode] = useState("individuals");
  const [selected, setSelected] = useState([]);
  const [department, setDepartment] = useState();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null);

  const departments = Array.from(
    new Set(staff.map((user) => normalizeStaffDepartment(user.department))),
  ).sort();
  const departmentCount = staff.filter(
    (user) => normalizeStaffDepartment(user.department) === department,
  ).length;
  const hasTarget =
    targetMode === "individuals" ? selected.length > 0 : departmentCount > 0;

  useEffect(() => {
    CommunicationService.getConversationStaff()
      .then(({ data }) =>
        setStaff(
          data.filter((user) => String(user.id) !== String(currentUser?.id)),
        ),
      )
      .catch(presentToastErrorContent);
  }, [currentUser?.id]);

  const notify = async () => {
    if (!hasTarget || sending) return;
    setSending(true);
    try {
      const { data } = await CommunicationService.notifyStaff(
        booking.bookingId,
        targetMode === "individuals"
          ? { type: "individuals", ids: selected.map((user) => user.id) }
          : { type: "department", department },
        message,
        photo,
      );
      onSent(data);
    } catch (error) {
      if (error.response?.data?.code === "MESSAGING_REQUIRED") onUpgrade();
      else presentToastErrorContent(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Layer
      onEsc={() => !sending && onClose()}
      onClickOutside={() => !sending && onClose()}
      responsive={false}
    >
      <Box
        width="large"
        style={{ maxWidth: "calc(100vw - 32px)", maxHeight: "90vh" }}
        overflow="auto"
        pad="large"
        gap="medium"
      >
        <Box direction="row" justify="between" align="center">
          <Heading level={3} margin="none">
            {t("conversations.notifyStaff")}
          </Heading>
          <Button
            icon={<FormClose />}
            disabled={sending}
            onClick={onClose}
            plain
          />
        </Box>
        <Text size="small" color="dark-5">
          {t("conversations.notifyDescription")}
        </Text>
        <div className="notify-target-tabs">
          <button
            type="button"
            className={targetMode === "individuals" ? "active" : ""}
            onClick={() => setTargetMode("individuals")}
          >
            {t("conversations.notifyPeople")}
          </button>
          <button
            type="button"
            className={targetMode === "department" ? "active" : ""}
            onClick={() => setTargetMode("department")}
          >
            {t("conversations.notifyDepartment")}
          </button>
        </div>
        {targetMode === "individuals" ? (
          <Select
            multiple
            closeOnChange={false}
            placeholder={t("conversations.selectStaff")}
            options={staff}
            labelKey="name"
            valueKey={{ key: "id", reduce: false }}
            value={selected}
            onChange={({ value }) => setSelected(value)}
          />
        ) : (
          <>
            <Select
              placeholder={t("conversations.selectDepartment")}
              options={departments.map((value) => ({ value, label: staffDepartmentLabel(value, t) }))}
              labelKey="label"
              valueKey={{ key: "value", reduce: true }}
              value={department}
              onChange={({ value }) => setDepartment(value)}
            />
            {department && (
              <Text size="small" color="dark-5">
                {t("conversations.departmentRecipients", {
                  count: departmentCount,
                })}
              </Text>
            )}
          </>
        )}
        <TextArea
          placeholder={t("conversations.internalMessagePlaceholder")}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          resize={false}
        />
        <MessagePhotoInput
          value={photo}
          onChange={setPhoto}
          disabled={sending}
        />
        <Button
          primary
          label={
            hasTarget
              ? t("conversations.notifyRecipients", {
                  count:
                    targetMode === "individuals"
                      ? selected.length
                      : departmentCount,
                })
              : t("conversations.notifyStaff")
          }
          icon={<User size="small" />}
          onClick={notify}
          disabled={!hasTarget || sending}
        />
      </Box>
    </Layer>
  );
}

function ReservationContext({ booking, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hotel } = useFetchHotel();
  const formatStayDateTime = (date) =>
    date ? moment.tz(date, hotel?.timezone).format("lll") : "-";
  const fields = [
    [t("conversations.reservationNumber"), booking.reservationNumber || "-"],
    [t("conversations.checkin"), formatStayDateTime(booking.checkinDate)],
    [t("conversations.checkout"), formatStayDateTime(booking.checkoutDate)],
    [t("conversations.room"), booking.roomNumber || "-"],
    [t("conversations.guests"), booking.guestCount || "-"],
    [
      t("conversations.status"),
      getBookingStatusLabel(booking.status, hotel?.serviceType, t) || "-",
    ],
  ];
  return (
    <aside className="conversations-context">
      <Box direction="row" justify="between" align="center">
        <Heading level={4} margin="none">
          {t("conversations.guestReservation")}
        </Heading>
        <Button
          className="conversations-context__close"
          icon={<FormClose />}
          plain
          onClick={onClose}
        />
      </Box>
      <Box
        direction="row"
        align="center"
        gap="small"
        margin={{ vertical: "medium" }}
      >
        <div
          className="conversation-avatar"
          style={{ backgroundColor: avatarColor(booking.guestName) }}
        >
          {initials(booking.guestName)}
        </div>
        <Box>
          <Text weight="bold">{booking.guestName}</Text>
          {booking.communicationPolicy?.reservation.archived && (
            <Text className="conversation-archived-badge" size="xsmall">
              {t("conversations.archived")}
            </Text>
          )}
          <Text size="small" color="dark-5">
            {booking.email || "-"}
          </Text>
          <Text size="small" color="dark-5">
            {booking.phone || "-"}
          </Text>
        </Box>
      </Box>
      <Box gap="small">
        {fields.map(([label, value]) => (
          <Box key={label} direction="row" justify="between" gap="small">
            <Text size="small" color="dark-5">
              {label}
            </Text>
            <Text size="small" weight="bold" textAlign="end">
              {value}
            </Text>
          </Box>
        ))}
      </Box>
      {booking.reservations?.length > 1 && (
        <Box gap="xsmall" margin={{ top: "large" }}>
          <Text size="small" weight="bold">
            {t("conversations.allReservations")}
          </Text>
          {booking.reservations.map((reservation) => (
            <Button
              key={reservation._id}
              plain
              onClick={() => navigate(`/guests/${reservation._id}`)}
              label={
                <Box
                  direction="row"
                  justify="between"
                  gap="small"
                  pad={{ vertical: "xsmall" }}
                >
                  <Text size="small">
                    {reservation.reservationNumber || "-"}
                  </Text>
                  <Text size="xsmall" color="dark-5">
                    {formatStayDateTime(reservation.checkinDate)} -{" "}
                    {formatStayDateTime(reservation.checkoutDate)}
                  </Text>
                </Box>
              }
            />
          ))}
        </Box>
      )}
      <Button
        margin={{ top: "large" }}
        label={t("conversations.viewReservation")}
        onClick={() => navigate(`/guests/${booking.bookingId}`)}
        secondary
      />
    </aside>
  );
}

export default function ConversationsPage() {
  const { hotel } = useFetchHotel();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [selected, setSelected] = useState();
  const [loading, setLoading] = useState(true);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [messagingUpgradeOpen, setMessagingUpgradeOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [externalMessage, setExternalMessage] = useState();
  const searchTimer = useRef();
  const requestId = useRef(0);
  const requestController = useRef();
  const unreadBookings = useRef(new Set());
  const selectedBookingIds = useRef([]);

  const load = useCallback(
    async (nextFilter = filter, nextSearch = search, nextPage = page) => {
      const currentRequestId = ++requestId.current;
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;
      setLoading(true);
      try {
        const { data } = await CommunicationService.getConversations({
          filter: nextFilter,
          search: nextSearch,
          page: nextPage,
          limit: PAGE_SIZE,
          signal: controller.signal,
        });
        if (currentRequestId !== requestId.current) return;
        unreadBookings.current = new Set(
          (data.conversations || [])
            .filter((row) => row.unreadCount > 0)
            .map((row) => row.guestId),
        );
        setRows(data.conversations || []);
        setCounts(data.counts || {});
        setPage(data.pagination?.page || nextPage);
        setTotalThreads(data.pagination?.total || 0);
        const requestedId = searchParams.get("booking");
        setSelected(
          (current) =>
            (requestedId &&
              data.conversations.find(
                (row) =>
                  row.bookingId === requestedId ||
                  row.bookingIds?.includes(requestedId),
              )) ||
            data.conversations.find(
              (row) => row.guestId === current?.guestId,
            ) ||
            (!window.matchMedia("(max-width: 760px)").matches &&
              data.conversations[0]),
        );
      } catch (error) {
        if (error.code !== "ERR_CANCELED") presentToastErrorContent(error);
      } finally {
        if (currentRequestId === requestId.current) setLoading(false);
      }
    },
    [filter, page, search, searchParams],
  );

  useEffect(() => {
    clearTimeout(searchTimer.current);
    setPage(1);
    load(filter, search, 1);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      clearTimeout(searchTimer.current);
      requestController.current?.abort();
    },
    [],
  );

  useEffect(() => {
    selectedBookingIds.current = selected?.bookingIds || [];
  }, [selected]);

  useEffect(() => {
    const handleHotelMessage = (event) => {
      const message = event.detail;
      if (message.isTestGuest) return;
      if (
        message.fromHotel ||
        selectedBookingIds.current.some(
          (bookingId) => String(message.booking) === String(bookingId),
        )
      ) {
        return;
      }
      load();
    };
    window.addEventListener("loxe:hotel-message", handleHotelMessage);
    return () =>
      window.removeEventListener("loxe:hotel-message", handleHotelMessage);
  }, [load]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(filter, value, 1), 350);
  };

  const handlePageChange = ({ page: nextPage }) => {
    setPage(nextPage);
    load(filter, search, nextPage);
  };

  const choose = (row) => {
    setExternalMessage(undefined);
    setSelected(row);
    setSearchParams({ booking: row.bookingId }, { replace: true });
  };

  const handleMessagesRead = useCallback(
    (conversationId) => {
      if (!unreadBookings.current.has(conversationId)) return;
      unreadBookings.current.delete(conversationId);
      setRows((currentRows) =>
        currentRows
          .map((row) =>
            row.guestId === conversationId ? { ...row, unreadCount: 0 } : row,
          )
          .filter(
            (row) => filter !== "unread" || row.guestId !== conversationId,
          ),
      );
      setSelected((current) =>
        current?.guestId === conversationId
          ? { ...current, unreadCount: 0 }
          : current,
      );
      setCounts((current) => ({
        ...current,
        unread: Math.max(0, (current.unread || 0) - 1),
      }));
    },
    [filter],
  );

  const refreshConversationIndex = () => {
    load();
  };

  return (
    <div
      className={`conversations-page${selected ? " conversations-page--selected" : ""}${contextOpen ? " conversations-page--context-open" : ""}`}
    >
      <section className="conversations-list">
        <Box pad="medium" gap="xsmall">
          <Heading level={2} margin="none">
            {t("conversations.title")}
          </Heading>
          <Text size="small" color="dark-5">
            {t("conversations.subtitle")}
          </Text>
        </Box>
        <div className="conversation-filters">
          {FILTERS.map((value) => (
            <button
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {t(`conversations.filters.${value}`)}
              {counts[value === "in-stay" ? "inStay" : value] !== undefined && (
                <span className={value === "unread" ? "unread-badge" : ""}>
                  {counts[value === "in-stay" ? "inStay" : value]}
                </span>
              )}
            </button>
          ))}
        </div>
        <Box pad={{ horizontal: "small", bottom: "small" }}>
          <TextInput
            icon={<Search />}
            placeholder={t("conversations.search")}
            value={search}
            onChange={(event) => handleSearch(event.target.value)}
          />
        </Box>
        <div className="conversation-rows">
          {loading ? (
            <Box align="center" pad="large">
              <Spinner />
            </Box>
          ) : rows.length === 0 ? (
            <Text
              className="conversation-empty"
              color="dark-5"
              textAlign="center"
            >
              {t("conversations.noResults")}
            </Text>
          ) : (
            rows.map((row) => (
              <button
                key={row.guestId}
                className={`conversation-row${selected?.guestId === row.guestId ? " active" : ""}`}
                onClick={() => choose(row)}
              >
                <div
                  className="conversation-avatar"
                  style={{ backgroundColor: avatarColor(row.guestName) }}
                >
                  {initials(row.guestName)}
                </div>
                <div className="conversation-row__body">
                  <div className="conversation-row__title">
                    <strong>{row.guestName}</strong>
                    {row.communicationPolicy?.reservation.archived && (
                      <span className="conversation-archived-badge">
                        {t("conversations.archived")}
                      </span>
                    )}
                    <time>
                      {row.lastMessage
                        ? formatLastMessageDate(row.lastMessage.createdAt, t)
                        : ""}
                    </time>
                  </div>
                  <div className="conversation-row__meta">
                    {t("conversations.roomRef", {
                      room: row.roomNumber || "-",
                      reservation: row.reservationNumber || "-",
                    })}
                  </div>
                  <div className="conversation-row__preview">
                    {row.hasConversation
                      ? isMessageLocked(row.lastMessage, row.messagingLocked) ? t("offers.lockedMessage") : row.lastMessage?.text || t("conversations.attachment")
                      : t("conversations.noConversation")}
                  </div>
                </div>
                {row.unreadCount > 0 && (
                  <span className="conversation-row__unread">
                    {row.unreadCount}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        {totalThreads > PAGE_SIZE && (
          <Box
            align="center"
            pad={{ vertical: "small" }}
            border={{ side: "top", color: "light-4" }}
          >
            <Pagination
              numberItems={totalThreads}
              step={PAGE_SIZE}
              page={page}
              onChange={handlePageChange}
            />
          </Box>
        )}
      </section>

      <main className="conversation-active">
        {selected ? (
          <>
            <header className="conversation-active__header">
              <Button
                className="conversation-active__back"
                icon={<FormPrevious />}
                plain
                onClick={() => setSelected(undefined)}
              />
              <Box flex>
                <Text weight="bold">{selected.guestName}</Text>
                <Text size="small" color="dark-5">
                  {t("conversations.activeMeta", {
                    room: selected.roomNumber || "-",
                    reservation: selected.reservationNumber || "-",
                    status:
                      getBookingStatusLabel(
                        selected.status,
                        hotel?.serviceType,
                        t,
                      ) || "-",
                  })}
                </Text>
              </Box>
              <Button
                label={t("conversations.notifyStaff")}
                icon={<User size="small" />}
                onClick={() => getOfferCapabilities(hotel).messaging ? setNotifyOpen(true) : setMessagingUpgradeOpen(true)}
              />
              <Button
                className="conversation-active__details"
                label={t("conversations.details")}
                onClick={() => setContextOpen(true)}
              />
            </header>
            <div className="conversation-active__thread">
              <Conversation
                key={selected.bookingId}
                bookingId={selected.bookingId}
                bookingIds={selected.bookingIds}
                reservations={selected.reservations}
                conversationId={selected.guestId}
                guestName={selected.guestName}
                phone={selected.phone}
                email={selected.email}
                embedded
                externalMessage={externalMessage}
                initialCommunicationPolicy={selected.communicationPolicy}
                onMessageSent={refreshConversationIndex}
                onMessagesRead={handleMessagesRead}
              />
            </div>
          </>
        ) : (
          <Box fill align="center" justify="center" gap="small">
            <Text weight="bold">{t("conversations.selectConversation")}</Text>
            <Text color="dark-5">
              {t("conversations.selectConversationHint")}
            </Text>
          </Box>
        )}
      </main>

      {selected && (
        <ReservationContext
          booking={selected}
          onClose={() => setContextOpen(false)}
        />
      )}
      {messagingUpgradeOpen && <PackageUpgradeModal capability="messaging" onClose={() => setMessagingUpgradeOpen(false)} />}
      {notifyOpen && selected && (
        <NotifyStaffModal
          booking={selected}
          onUpgrade={() => { setNotifyOpen(false); setMessagingUpgradeOpen(true); }}
          onClose={() => setNotifyOpen(false)}
          onSent={(message) => {
            setNotifyOpen(false);
            setExternalMessage(message);
            refreshConversationIndex();
          }}
        />
      )}
    </div>
  );
}
